import { useForm, Link, usePage } from '@inertiajs/react'
import { FormEvent, useState } from 'react'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import AuthLayout, {
  AuthCardHeading,
  AuthMessage,
  BackToSignIn,
} from '@/layouts/AuthLayout'
import { PageProps } from '@/types'

interface ResetPasswordProps extends PageProps {
  reset_password_token: string
  minimum_password_length: number
}

export default function ResetPassword() {
  const { reset_password_token, minimum_password_length, flash } =
    usePage<ResetPasswordProps>().props

  const { data, setData, put, processing, errors } = useForm({
    user: {
      reset_password_token: reset_password_token || '',
      password: '',
      password_confirmation: '',
    },
  })

  const [showPassword, setShowPassword] = useState(false)

  const err = (key: string) => {
    const fieldErrors = errors as Record<string, string>
    return fieldErrors[key] || fieldErrors[`user.${key}`]
  }

  // Devise reports an expired / already-used / tampered link as an error on
  // the token itself. That is not something the user can fix on this form, so
  // show a dedicated state instead of the fields.
  const tokenError = err('reset_password_token')
  const linkUnusable = Boolean(tokenError) || !reset_password_token

  const formError = flash?.alert || flash?.error || null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    put('/users/password')
  }

  if (linkUnusable) {
    return (
      <AuthLayout title="Choose a New Password">
        <AuthCardHeading
          title="This reset link is no longer valid"
          description={
            <>
              {tokenError
                ? String(tokenError)
                : 'This link is missing its security token.'}{' '}
              Reset links expire for your safety, and each one can only be used
              once. Request a fresh email to continue.
            </>
          }
        />

        <Link
          href="/password/reset"
          className="auth-submit inline-flex text-center"
        >
          Request a new reset link
        </Link>

        <BackToSignIn />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Choose a New Password">
      <AuthCardHeading
        title="Choose a new password"
        description={`Pick a password of at least ${minimum_password_length} characters.`}
      />

      {formError && <AuthMessage tone="error">{formError}</AuthMessage>}

      <form onSubmit={handleSubmit} className="space-y-5">
        <input
          type="hidden"
          name="reset_password_token"
          value={data.user.reset_password_token}
        />

        <div>
          <div className="flex items-baseline justify-between">
            <label htmlFor="password" className="auth-label">
              New password
            </label>
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              className="auth-link mb-1.5 inline-flex items-center gap-1 text-xs"
              aria-pressed={showPassword}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <>
                  <EyeOff className="h-3.5 w-3.5" aria-hidden="true" /> Hide
                </>
              ) : (
                <>
                  <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Show
                </>
              )}
              <span className="sr-only"> password</span>
            </button>
          </div>
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            autoFocus
            minLength={minimum_password_length}
            value={data.user.password}
            onChange={(e) => setData('user', { ...data.user, password: e.target.value })}
            className="auth-input"
            aria-invalid={Boolean(err('password'))}
            aria-describedby={err('password') ? 'password-error' : undefined}
          />
          {err('password') && (
            <p id="password-error" className="mt-1.5 text-sm text-status-error">
              {err('password')}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="password_confirmation" className="auth-label">
            Confirm new password
          </label>
          <input
            id="password_confirmation"
            name="password_confirmation"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={data.user.password_confirmation}
            onChange={(e) =>
              setData('user', { ...data.user, password_confirmation: e.target.value })
            }
            className="auth-input"
            aria-invalid={Boolean(err('password_confirmation'))}
            aria-describedby={
              err('password_confirmation') ? 'password-confirmation-error' : undefined
            }
          />
          {err('password_confirmation') && (
            <p
              id="password-confirmation-error"
              className="mt-1.5 text-sm text-status-error"
            >
              {err('password_confirmation')}
            </p>
          )}
        </div>

        <button type="submit" disabled={processing} className="auth-submit">
          {processing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Saving&hellip;
            </>
          ) : (
            'Change my password'
          )}
        </button>
      </form>

      <BackToSignIn />
    </AuthLayout>
  )
}
